import { Component, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CardComponent, StepperComponent, StepComponent, ButtonComponent } from '@webapp/ui';
import { RiskLoaderComponent } from '@webapp/quoter';
import { IssuePolicyStore } from '../../store/issue-policy-store.service';
import { IUser } from '@webapp/users/interfaces/users.interface';
import { IssuePolicyPaymentComponent } from '../components/issue-policy-payment/issue-policy-payment.component';
import { IPayment } from '@webapp/quoter/interfaces/payment.interface';
import { FlowResumeStepComponent } from '../../components/flow-resume-step/flow-resume-step.component';
import { RiskContext } from '@webapp/risks/strategies/risk-context';
import { DynamicRiskStep2Component } from '@webapp/risks/components/dynamic-risk-step2/dynamic-risk-step2.component';
import { IRiskStepFormData } from '@webapp/risks/interfaces/risk-step-component.interface';
import { UserDataStepComponent } from '../../components/user-data-step/user-data-step.component';
import { UsersService } from '@webapp/users/services/users.service';
import { ToastService } from '@webapp/ui/components/toast/toast.service';
import { QuotesService } from '@webapp/quotes/services/quotes.service';
import { Router } from '@angular/router';
import { IClause, IEmbeddedQr } from '@webapp/qrs/interfaces/qrs.interface';
import { RiskFieldData, RiskRegistry } from '@webapp/risk-core';
import { WEB_RISK_SLOTS } from '@webapp/risks/web';


@Component({
  selector: 'app-issue-policy-page',
  imports: [CardComponent, StepperComponent, StepComponent, DynamicRiskStep2Component, RiskLoaderComponent, IssuePolicyPaymentComponent, FlowResumeStepComponent, ButtonComponent, UserDataStepComponent],
  templateUrl: './issue-policy-page.component.html',
  styleUrl: './issue-policy-page.component.css'
})
export class IssuePolicyPageComponent {
  protected readonly store = inject(IssuePolicyStore);
  private readonly riskContext = inject(RiskContext);
  private readonly riskRegistry = inject(RiskRegistry);
  private readonly usersService = inject(UsersService);
  private readonly toastService = inject(ToastService);
  private readonly quotesService = inject(QuotesService);
  private router = inject(Router);

  // ── QR mandatory clauses (transient — read from the embedded qr object) ──

  private readonly qrClauses = computed<IClause[]>(() => {
    const insurable = this.insurableData() as { qr?: IEmbeddedQr } | null;
    return insurable?.qr?.clauses ?? [];
  });

  // Computed properties for template access
  protected readonly user = computed(() => this.store.quote()?.user ?? null);
  protected readonly risk = computed(() => this.store.quote()?.risk ?? null);
  protected readonly loading = computed(() => this.store.state()?.isLoading ?? false);
  protected readonly emitting = computed(() => this.store.isEmitting());
  protected readonly hasValidData = computed(() => {
    const quote = this.store.quote();
    return quote && quote.user && quote.risk && this.store.steps().length > 0;
  });

  // Computed properties for the resume step
  protected readonly insurableData = computed(() => {
    const quote = this.store.quote();
    if (!quote) return null;
    const insurable = this.riskContext.getInsurableData(quote);
    if (!insurable) return null;
    const currency = (insurable as Record<string, unknown>)['currency'] ?? quote.currency;
    return { ...insurable, currency };
  });

  protected readonly currency = computed(() => this.store.quote()?.currency ?? 'ARS');

  protected readonly selectedCoverage = computed(() =>
    this.store.selectedCoverage()
    ?? this.store.quote()?.coverages?.find(c => c.select)
    ?? this.store.quote()?.coverages?.[0]
    ?? null
  );

  protected readonly additionalCoverage = computed(() => {
    const quote = this.store.quote();
    // Mandatory garage coverage (quads: added by the backend) is always shown
    const isMandatory = this.riskRegistry.find(quote?.risk?.tag)?.capabilities.garage === 'mandatory';
    const isSelected = isMandatory || this.store.selectedAdditionalCoverage();
    return (isSelected && quote?.additionalCoverage?.id && quote.additionalCoverage) || null;
  });

  protected readonly paymentMethod = computed(() => {
    return this.store.getPaymentData() || null;
  });

  protected readonly quoteInfo = computed(() => {
    const quote = this.store.quote();
    return quote ? {
      id: quote.id,
      internalNumber: quote.internalNumber
    } : null;
  });

  protected readonly riskType = computed(() => {
    const risk = this.risk();
    return risk ? {
      id: risk.id,
      name: risk.name,
      tag: risk.tag
    } : null;
  });

  // Current risk type for dynamic component
  protected readonly currentRiskType = computed(() => {
    const risk = this.risk();
    return risk?.tag || null;
  });

  /**
   * Non-null when the risk contributes an `insurable-detail` slot. The resume
   * step resolves and mounts the slot itself from the risk tag; this value only
   * tells it that the risk has a detail to show.
   */
  readonly detailComponentType = computed<string | null>(() => {
    const tag = this.risk()?.tag;
    if (!tag) return null;
    return this.riskRegistry.slot('web', tag, WEB_RISK_SLOTS.INSURABLE_DETAIL) ? WEB_RISK_SLOTS.INSURABLE_DETAIL : null;
  });

  readonly detailData = computed<unknown>(() => {
    const fromQuote = this.insurableData() ?? {};
    const fromStore = this.store.issuePolicyInsurableData() ?? {};
    return { ...fromQuote, ...fromStore, qrClauses: this.qrClauses() };
  });

  // Initial data for the dynamic risk component
  protected readonly step2InitialData = computed(() => {
    return this.store.issuePolicyInsurableData() || {};
  });

  // ── Referral code (transient — never written to store) ───────────────────

  readonly isPaymentAddMode = signal(false);
  readonly referralCode = signal<string | null>(null);
  protected readonly referralCodeValidatingSignal = signal(false);

  readonly canContinueFromResume = computed(() => !this.referralCodeValidatingSignal());

  onReferralCodeChanged = (code: string | null): void => {
    this.referralCode.set(code);
  };

  onReferralCodeValidating = (busy: boolean): void => {
    this.referralCodeValidatingSignal.set(busy);
  };

  // ── End referral code ─────────────────────────────────────────────────────

  updateUser = async (user: IUser) => {
    const success = await this.store.updateUser(user);
    if (success) {
      this.store.completeCurrentStep();
      this.store.nextStep();
    }
  };

  setRiskData = (fields: Array<{ name: string, value: unknown }>) => {
    if (fields.length > 0) {
      // Transform the fields to match RiskFieldData interface
      const riskFieldsData: RiskFieldData[] = fields.map(field => ({
        name: field.name,
        value: field.value
      }));

      this.store.setIssuePolicyInsurableData(riskFieldsData);
      this.store.completeCurrentStep();
      this.store.nextStep();

    }
  };

  onStep2FormDataChanged = async (formData: IRiskStepFormData) => {
    const riskFieldsData: RiskFieldData[] = Object.entries(formData.data).map(([name, value]) => ({
      name,
      value
    }));

    this.store.setIssuePolicyInsurableData(riskFieldsData);

    const insuredSaved = await this.store.saveInsurableData();
    if (!insuredSaved) return;
    const clausesSaved = await this.store.saveClausesData();
    if (!clausesSaved) return;
    this.store.completeCurrentStep();
    this.store.nextStep();
  };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onStep2ValidationChanged = (_isValid: boolean) => {};

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onStep2ComponentReady = (_isReady: boolean) => {};

  // Method to proceed to next step (called from button or when form is complete)
  proceedFromStep2 = () => {
    this.store.completeCurrentStep();
    this.store.nextStep();
  };

  setPaymentData = (payment: IPayment) => {
    // Store the payment data and proceed to next step
    this.store.setPaymentData(payment);
    this.store.completeCurrentStep();
    this.store.nextStep();
  };

  continueWithoutUpdate = async () => {
    const user = this.user();

    // Check if user is a prospect with complete data and convert to client
    if (user && !user.isClient && user.id) {
      if (this.usersService.validateUserData(user)) {
        try {
          await firstValueFrom(this.usersService.convertProspectToClient(user.id));
          this.toastService.showToast(
            'Prospecto convertido a cliente',
            'El prospecto se ha convertido exitosamente a cliente.',
            'success'
          );
          await this.store.refreshQuote();
        } catch (convertError) {
          console.error('Error converting prospect to client:', convertError);
          this.toastService.showToast(
            'Advertencia',
            'No se pudo convertir a cliente automáticamente.',
            'alert_warning'
          );
        }
      }
    }

    this.store.completeCurrentStep();
    this.store.nextStep();
  };

  confirmPolicy = async () => {
    const quote = this.store.quote();
    const paymentData = this.store.getPaymentData();
    if (!quote) {
      console.error('No quote data available');
      return;
    }

    if (!paymentData) {
      console.error('No payment data available');
      return;
    }

    if (!quote.user?.id && !quote.user) {
      console.error('No user data available');
      return;
    }

    if (paymentData.id === 'mercado_pago_link') {
      return this.handleLinkGeneration();
    }

    if (paymentData.id === 'mercado_pago_cash') {
      return this.handleAccountMoneyEmission();
    }

    return this.handleStandardEmission();
  };

  private handleLinkGeneration = async (): Promise<void> => {
    const success = await this.store.generatePaymentLink(this.referralCode());
    if (success) {
      this.router.navigate(['polizas', 'nueva', this.store.quote()?.id, 'link-generado']);
    }
  };

  private handleStandardEmission = async (): Promise<void> => {
    const success = await this.store.emitPolicy(this.referralCode());
    if (success) {
      this.router.navigate(['polizas', 'nueva', this.store.quote()?.id, 'success']);
    }
  };

  private handleAccountMoneyEmission = async (): Promise<void> => {
    const response = await this.store.emitPolicyAccountMoney(this.referralCode());

    if (!response) {
      return;
    }

    if (response.initPoint) {
      window.location.href = response.initPoint;
      return;
    }

    console.warn('emitPolicyAccountMoney: initPoint missing in response', response);
    this.toastService.showToast(
      'Advertencia',
      'La póliza se emitió correctamente. No pudimos redirigirte a Mercado Pago; podés acceder desde tu panel.',
      'alert_warning'
    );
    this.router.navigate(['polizas', 'nueva', this.store.quote()?.id, 'success']);
  };

  onClauseRemoved = async (clauseId: string): Promise<void> => {
    const quoteId = this.store.quote()?.id;
    if (!quoteId) return;
    try {
      await firstValueFrom(this.quotesService.deleteClause(quoteId, clauseId));
      await this.store.refreshQuote();
      this.removeClauseFromStore(clauseId);
      this.toastService.showToast('Cláusula eliminada', 'La cláusula se eliminó correctamente.', 'success');
    } catch (error) {
      console.error('Error removing clause:', error);
      await this.store.refreshQuote();
      this.toastService.showToast('Error', 'No se pudo eliminar la cláusula. Intente nuevamente.', 'error');
    }
  };

  private removeClauseFromStore(clauseId: string): void {
    const current = this.store.issuePolicyInsurableData() as Record<string, unknown> | null;
    if (!current) return;
    const clauses = (current['clauses'] as { id?: string }[] ?? [])
      .filter(c => c.id !== clauseId);
    this.store.setIssuePolicyInsurableData([{ name: 'clauses', value: clauses }]);
  }

  goBack = () => {
    // Go to previous step by decrementing current step
    const currentStep = this.store.currentStep();
    if (currentStep > 1) {
      this.store.setCurrentStep(currentStep - 1);
    }
  };

  goNext = () => {
    this.store.nextStep();
  };
}